import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import {
  buildArraySorter,
  buildQueryFilters,
  CurrentUserOrganizationDto,
  useInfiniteOrganizations,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import { useUserSetupStore } from '@sib/auth';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { useDebouncedValue } from '@sib/utils';
import { paths } from '@spendbrain/config/paths';

const PAGE_SIZE = 100;

const getOrganizationNameFilter = (searchTerm: string) =>
  searchTerm
    ? buildQueryFilters([
        {
          field: 'name',
          operator: 'contains',
          value: searchTerm,
        },
      ])
    : [];

export interface OrganizationSwitchDialogProps {
  onClose: () => void;
  open: boolean;
}

export const useOrganizationSwitch = ({
  onClose,
}: OrganizationSwitchDialogProps) => {
  const {
    organizations,
    organization: { id: organizationId, name: organizationName },
    setEntryPointOrganization,
    setOrganization,
  } = useUserSetupStore();
  const [selectedOrganization, setSelectedOrganization] = useReducer(
    (
      _: CurrentUserOrganizationDto | null,
      nextOrganization: CurrentUserOrganizationDto | null
    ) => nextOrganization,
    null
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const { open: showNotification } = useNotification();

  const {
    data: userOrganizations,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteOrganizations({
    page: DEFAULT_PAGE,
    size: PAGE_SIZE,
    filter: getOrganizationNameFilter(debouncedSearch),
    sort: buildArraySorter({
      field: 'name',
      order: 'asc',
    }),
  });

  const filteredOrganizations = useMemo(
    () => userOrganizations?.pages.map(page => page.items).flat() || [],
    [userOrganizations]
  );

  const totalRows = useMemo(
    () => userOrganizations?.pages[0].totalItems || 0,
    [userOrganizations]
  );

  const defaultOrganization = useMemo(
    () => ({ id: organizationId, name: organizationName }),
    [organizationId, organizationName]
  );

  const switchOrganization = useCallback(() => {
    const newOrganizationId = selectedOrganization?.id || '';

    setOrganization({
      id: newOrganizationId,
      name: selectedOrganization?.name || '',
    });
    setEntryPointOrganization(null);
    onClose();

    const isOnTicketsPage = pathname.includes(paths.tickets.client.root);
    if (isOnTicketsPage && newOrganizationId)
      navigate(
        generatePath(paths.tickets.employee.root, { orgId: newOrganizationId }),
        {
          replace: true,
        }
      );

    showNotification({
      message: t('components.organizationSwitchDialog.successMessage', {
        organization: defaultOrganization?.name,
        newOrganization: selectedOrganization?.name,
      }),
      type: 'success',
    });
  }, [
    onClose,
    selectedOrganization,
    showNotification,
    setOrganization,
    t,
    defaultOrganization,
    setEntryPointOrganization,
    pathname,
    navigate,
  ]);

  const onDialogClose = useCallback(() => {
    onClose();
    setSearch('');
    setSelectedOrganization(defaultOrganization);
  }, [onClose, defaultOrganization]);

  useEffect(() => {
    if (!defaultOrganization) return;
    setSelectedOrganization(defaultOrganization);
  }, [defaultOrganization]);

  return {
    onSearch: setSearch,
    organizations,
    switchOrganization,
    filteredOrganizations,
    defaultOrganization,
    search,
    selectedOrganization,
    onDialogClose,
    setSelectedOrganization,
    isLoading: isFetching && !isFetchingNextPage,
    infiniteList: {
      fetchNextPage,
      totalRows,
      hasMore: hasNextPage,
      dataLength: filteredOrganizations.length,
    },
  };
};
